import { Fragment, useEffect } from 'react';

import {
  Box,
  IconButton,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { CheckedIcon, InfoIcon } from '@udacity/chakra-uds-icons';

import { Markdown } from '~/components/markdown';
import { PlansColumns, PlansComparisonSection } from '~/features/plans/models/plans-comparison';

export type PlansMatrixProps = {
  columns: PlansColumns;
  sections: PlansComparisonSection[];
};

const BORDER_COLOR = 'grey.200';

// The table fills its card edge-to-edge (the card has no padding), so section-header bands span
// the full width without any bleed hacks. Cells carry their own horizontal padding instead; edge
// columns get a larger inset so content never sits flush against the card border.
const FEATURE_COL_WIDTH = { base: '260px', lg: '46%' };
const EDGE_X = { base: 5, lg: 8 };
const INNER_X = { base: 2, lg: 4 };

export function PlansMatrix({ columns, sections }: PlansMatrixProps) {
  const lastColumnIndex = columns.length - 1;

  return (
    <TableContainer
      overflowX={{ base: 'auto', lg: 'visible' }}
      overflowY={{ base: 'hidden', lg: 'visible' }}
      // The table can't fit narrow viewports, so it scrolls horizontally on mobile; this enables
      // momentum scrolling for that on iOS.
      sx={{ WebkitOverflowScrolling: 'touch' }}
    >
      <Table
        variant='unstyled'
        sx={{
          tableLayout: 'fixed',
          minWidth: { base: '680px', lg: 'auto' },
        }}
      >
        <TableCaption margin={0} padding={0}>
          <VisuallyHidden>Compare Udacity plans</VisuallyHidden>
        </TableCaption>
        <Thead>
          <Tr>
            <Th paddingLeft={EDGE_X} paddingY={{ base: 4, lg: 5 }} width={FEATURE_COL_WIDTH}>
              <VisuallyHidden>Features</VisuallyHidden>
            </Th>
            {columns.map((column, index) => (
              <Th
                key={column.id}
                paddingLeft={INNER_X}
                paddingRight={index === lastColumnIndex ? EDGE_X : INNER_X}
                paddingY={{ base: 4, lg: 5 }}
                textAlign='center'
                textTransform='none'
                verticalAlign='bottom'
              >
                <Box
                  color='grey.900'
                  fontFamily='heading'
                  fontSize={{ base: 'md', lg: 'xl' }}
                  fontWeight='600'
                  letterSpacing='-0.01em'
                >
                  {column.title}
                </Box>
                <Box
                  bgColor='blue.500'
                  borderRadius='full'
                  height='3px'
                  margin='10px auto 0'
                  width='28px'
                  aria-hidden
                />
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sections.map((section, sectionIndex) => (
            <Fragment key={section.title}>
              <Tr>
                <Th
                  borderColor={BORDER_COLOR}
                  borderTop={sectionIndex === 0 ? undefined : '1px solid'}
                  colSpan={columns.length + 1}
                  color='grey.600'
                  fontFamily='heading'
                  fontSize='sm'
                  fontWeight='700'
                  letterSpacing='0.06em'
                  paddingBottom={3}
                  paddingLeft={EDGE_X}
                  paddingTop={sectionIndex === 0 ? { base: 2, lg: 3 } : { base: 8, lg: 10 }}
                  textAlign='left'
                  textTransform='uppercase'
                >
                  {section.title}
                </Th>
              </Tr>
              {section.rows.map((row) => (
                <Tr key={row.title} _hover={{ bgColor: 'grey.50' }} transition='background-color 0.15s ease'>
                  <Td
                    borderBottom='1px solid'
                    borderColor='grey.100'
                    color='grey.900'
                    fontSize={{ base: 'sm', lg: 'md' }}
                    fontWeight='500'
                    lineHeight='1.4'
                    paddingLeft={EDGE_X}
                    paddingRight={INNER_X}
                    paddingY={4}
                    whiteSpace='normal'
                  >
                    <Box as='span'>{row.title}</Box>
                    {row.description && <Information description={row.description} title={row.title} />}
                  </Td>
                  {columns.map((column, index) => (
                    <Td
                      key={column.id}
                      borderBottom='1px solid'
                      borderColor='grey.100'
                      color='grey.700'
                      fontSize={{ base: 'sm', lg: 'md' }}
                      lineHeight='1.4'
                      paddingLeft={INNER_X}
                      paddingRight={index === lastColumnIndex ? EDGE_X : INNER_X}
                      paddingY={4}
                      textAlign='center'
                      whiteSpace='normal'
                    >
                      <PlanCellValue value={row.plans[column.id]} />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function PlanCellValue({ value }: { value: string | boolean | undefined }) {
  if (typeof value === 'boolean' || value === undefined) {
    return value === true ? (
      <>
        <CheckedIcon color='blue.500' height={6} width={6} aria-hidden />
        <VisuallyHidden>Included</VisuallyHidden>
      </>
    ) : (
      <>
        <Box as='span' color='grey.300' aria-hidden>
          &mdash;
        </Box>
        <VisuallyHidden>Not included</VisuallyHidden>
      </>
    );
  }

  return (
    <Box sx={{ a: { color: 'blue.500', textDecoration: 'underline' } }}>
      <Markdown markdownText={value} />
    </Box>
  );
}

function Information({ title, description }: { title: string; description: string }) {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Close the tooltip on scroll, otherwise it can overlap the sticky nav.
    const scrollHandler = onClose;

    if (isOpen) {
      window.addEventListener('keydown', keydownHandler);
      window.addEventListener('scroll', scrollHandler);
      return () => {
        window.removeEventListener('keydown', keydownHandler);
        window.removeEventListener('scroll', scrollHandler);
      };
    }
  }, [isOpen, onClose]);

  return (
    <Tooltip isOpen={isOpen} label={description} hasArrow>
      <IconButton
        aria-label={`More information about "${title}".`}
        display='inline-flex'
        height='unset'
        icon={<InfoIcon color='grey.500' height={4} width={4} />}
        minWidth='unset'
        ml={1}
        variant='ghost'
        verticalAlign='text-bottom'
        onBlur={onClose}
        onClick={onToggle}
        onFocus={onOpen}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      />
    </Tooltip>
  );
}
